import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Hero } from "@/components/Hero";
import { MaintenanceBanner } from "@/components/MaintenanceBanner";
import { Marquee } from "@/components/Marquee";
import { PromoBanner } from "@/components/PromoBanner";
import { FeaturedGrid } from "@/components/FeaturedGrid";
import { Manifesto } from "@/components/Manifesto";
import { Owners } from "@/components/Owners";
import { ReviewStrip } from "@/components/ReviewStrip";
import { Reveal } from "@/components/Reveal";
import { scrollToEl } from "@/lib/scroll";
import { INFOGRAPHIC_IMAGE } from "@/lib/data";

export default function HomePage() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash === "#manifesto") {
      const t = setTimeout(() => scrollToEl("#manifesto"), 500);
      return () => clearTimeout(t);
    }
  }, [location.hash]);

  return (
    <main>
      <MaintenanceBanner />
      <Hero />
      <Marquee />
      <PromoBanner />
      <FeaturedGrid />
      <Manifesto />
      <Owners />
      <ReviewStrip />
      <section className="bg-white py-24 sm:py-28" data-testid="infographic-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <Reveal className="text-center">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-royal">
              The old way vs the Oregon way
            </p>
            <h2 className="mx-auto mt-4 max-w-2xl font-display text-2xl font-bold leading-tight tracking-tight text-ink sm:text-3xl lg:text-4xl">
              Ditch the cardboard chaos.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mx-auto mt-12 max-w-5xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/10 transition-transform duration-500 hover:scale-[1.01]">
              <img
                src={INFOGRAPHIC_IMAGE}
                alt="Oregon Tote Rentals — the old cardboard hassle versus heavy-duty plastic totes with friendly scheduled delivery and pick-up"
                className="w-full"
              />
            </div>
          </Reveal>
        </div>
      </section>
      <section className="relative overflow-hidden bg-royal py-24 sm:py-28">
        <div
          aria-hidden
          className="text-outline-light pointer-events-none absolute -top-8 left-0 select-none whitespace-nowrap font-display text-[14vw] font-extrabold leading-none opacity-60"
        >
          MOVE READY
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <Reveal>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-white/70">
              Free delivery over $25 · Pickup always included
            </p>
            <h2 className="mt-5 max-w-3xl font-display text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">
              Your move starts with{" "}
              <span className="font-serif font-normal italic">a tote.</span>
            </h2>
            <Link
              to="/catalog"
              data-testid="cta-browse-catalog"
              className="mt-9 inline-flex items-center gap-2.5 rounded-full bg-white px-8 py-4 text-sm font-bold text-royal transition-all duration-200 hover:scale-[1.03] hover:shadow-2xl hover:shadow-ink/30 active:scale-[0.98]"
            >
              Browse packages <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
